import React, { useState } from "react";
import { ArrowRight, ShieldCheck, TrendingUp, Target, Mail, Lock, User } from "lucide-react";
import SpiderLogo from "../components/SpiderLogo";
import SuitTrailer from "../components/SuitTrailer";
import SuitPicker from "../components/SuitPicker";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function apiErrorText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  // After signup, show a "Set your suit" step before diving into the dashboard
  const [showSuitStep, setShowSuitStep] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const r = await api.post(url, body);
      setUser(r.data.user);
      if (mode === "register") {
        // Offer the suit picker before entering the app
        setShowSuitStep(true);
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      setErr(apiErrorText(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true);
    } catch { setForgotDone(true); }
    finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B1220] relative overflow-hidden">
      {showSuitStep && (
        <SuitPicker
          userName={name}
          onDone={() => navigate("/dashboard", { replace: true })}
        />
      )}
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl" style={{ background: "#DC1F26" }}></div>
      <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl" style={{ background: "#0A1D4F" }}></div>

      <div className="relative max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-spidey-red grid place-items-center">
              <SpiderLogo className="w-6 h-6" color="white" />
            </div>
            <span className="font-display text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Argent</span>
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-400 tracking-widest uppercase font-bold">v1.0</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mt-12 lg:mt-16">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 mb-6">
              <ShieldCheck className="w-3.5 h-3.5 spidey-red" />
              With great power comes a great responsibility with your money.
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-100 leading-[0.95] tracking-tight">
              Track your money, <br/>
              <span className="italic font-medium spidey-red">web-slinger</span> style.
            </h1>
            <p className="mt-5 text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-md">
              Swing between transactions, budgets and savings goals — with a dashboard that feels heroic to open.
            </p>
            <div className="max-w-md">
              <SuitTrailer />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-lg shadow-slate-900/5">
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-full p-1 mb-6">
              <button
                data-testid="mode-login-btn"
                onClick={() => { setMode("login"); setErr(""); }}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "login" ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
              >Log in</button>
              <button
                data-testid="mode-register-btn"
                onClick={() => { setMode("register"); setErr(""); }}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "register" ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
              >Sign up</button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <div>
                  <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Name</Label>
                  <div className="relative mt-1.5">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      data-testid="signup-name-input"
                      className="pl-9 rounded-xl"
                      value={name} onChange={e => setName(e.target.value)}
                      placeholder="Peter Parker"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="email-input"
                    type="email"
                    required
                    className="pl-9 rounded-xl"
                    value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="password-input"
                    type="password"
                    required
                    minLength={mode === "register" ? 8 : 1}
                    className="pl-9 rounded-xl"
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                  />
                </div>
              </div>

              {err && (
                <div data-testid="auth-error" className="text-xs font-semibold text-rose-600 bg-rose-50 dark:bg-rose-900/30 border border-rose-100 dark:border-rose-800/60 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}

              {mode === "login" && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    data-testid="forgot-open-btn"
                    onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotDone(false); }}
                    className="text-xs font-semibold spidey-red hover:underline"
                  >Forgot password?</button>
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                data-testid="submit-auth-btn"
                className="w-full rounded-full bg-spidey-red text-white hover:opacity-90 font-bold h-12"
              >
                {busy ? "Working…" : mode === "login" ? "Log in" : "Create account"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800"></div>
              <span className="text-xs font-semibold uppercase text-slate-400 dark:text-slate-500">or</span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800"></div>
            </div>

            <button
              data-testid="google-login-btn"
              onClick={handleGoogle}
              type="button"
              className="w-full inline-flex items-center justify-center gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 px-4 py-3 rounded-full font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#DC1F26" d="M21.35 11.1H12v3.2h5.35c-.24 1.5-1.65 4.4-5.35 4.4-3.22 0-5.85-2.66-5.85-5.95S8.78 6.8 12 6.8c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.7 4.24 14.55 3.3 12 3.3 6.98 3.3 2.9 7.38 2.9 12.4c0 5.02 4.08 9.1 9.1 9.1 5.25 0 8.72-3.68 8.72-8.87 0-.6-.06-1.05-.14-1.53z"/></svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-4 mt-6 text-[11px] text-slate-500 dark:text-slate-400">
              <div className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3 spidey-red" /> Bank-grade encryption</div>
              <div className="inline-flex items-center gap-1"><Target className="w-3 h-3 spidey-red" /> Free forever</div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot password modal */}
      {forgotOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center px-4" onClick={() => setForgotOpen(false)}>
          <div
            data-testid="forgot-modal"
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-md p-6 border border-slate-200 dark:border-slate-800 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-spidey-red grid place-items-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Recover access</div>
                <div className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">Reset your password</div>
              </div>
            </div>

            {forgotDone ? (
              <div data-testid="forgot-success" className="mt-4 text-sm text-slate-700 dark:text-slate-300">
                If an account exists for <span className="font-semibold">{forgotEmail}</span>, we've sent a reset link to that inbox. Check your email (or the app server console during development).
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setForgotOpen(false)} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="forgot-close-btn">Got it</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={sendForgot} className="mt-3 space-y-3">
                <p className="text-sm text-slate-500 dark:text-slate-400">Enter your email and we'll send a link to reset your password.</p>
                <div>
                  <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      data-testid="forgot-email-input"
                      type="email" required
                      className="pl-9 rounded-xl"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} className="rounded-full" data-testid="forgot-cancel-btn">Cancel</Button>
                  <Button type="submit" disabled={forgotBusy} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="forgot-submit-btn">
                    {forgotBusy ? "Sending…" : "Send reset link"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
